import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, Button, IconButton, Tooltip, TextField,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import RouteIcon from '@mui/icons-material/Route';
import PlaceIcon from '@mui/icons-material/Place';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useTranslation } from 'react-i18next';
import { useMessage } from '../contexts/MessageContext';
import { type TripPlan, type TripAttraction, TRANSPORT_OPTIONS, normalizeTrips } from '../services/tripService';
import { loadLifeData, saveTripPlans, uploadTripPhoto,
         tripPhotoKey, resolveTripPhoto, type TripPhoto } from '../services/lifeDataService';
import AuthImage from '../components/AuthImage';
import { fetchWeatherForDate, type DayForecast } from '../services/attractionService';

// ── Stop card (sub-component to carry local editing state) ────────────────────

interface StopCardProps {
  attr: TripAttraction;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isZh: boolean;
  onMove: (dir: 'up' | 'down') => void;
  onUpdateField: (field: string, value: any) => void;
  onRemove: () => void;
}

const StopCard: React.FC<StopCardProps> = ({
  attr, index, isFirst, isLast, isZh, onMove, onUpdateField, onRemove,
}) => {
  const { showApiError, showSuccess } = useMessage();
  const [notes, setNotes]         = useState(attr.notes ?? '');
  const [parts, setParts]         = useState(attr.participants != null ? String(attr.participants) : '');
  const [uploading, setUploading] = useState(false);
  const photoInputRef             = React.useRef<HTMLInputElement>(null);

  useEffect(() => { setNotes(attr.notes ?? ''); }, [attr.notes]);
  useEffect(() => { setParts(attr.participants != null ? String(attr.participants) : ''); }, [attr.participants]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const photo = await uploadTripPhoto(file);
      onUpdateField('photos', [...(attr.photos ?? []), photo]);
      showSuccess('SYS310004');
    } catch (e) { showApiError(e); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleRemovePhoto = (p: TripPhoto) => {
    const k = tripPhotoKey(p);
    onUpdateField('photos', (attr.photos ?? []).filter(x => tripPhotoKey(x) !== k));
  };

  const name = isZh ? (attr.nameZH || attr.nameEN) : (attr.nameEN || attr.nameZH);
  const transport = TRANSPORT_OPTIONS.find(o => o.key === attr.transport);

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: '1px solid #f0e6ff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      {/* ── Header row: badge / arrows / info / thumbnail / actions ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px 6px' }}>

        {/* Order badge + move buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: 1 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%',
            background: '#ede7f6', color: '#9b59b6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700,
          }}>
            {index + 1}
          </div>
          <IconButton size="small" onClick={() => onMove('up')} disabled={isFirst}
            sx={{ p: '2px', color: isFirst ? '#e0e0e0' : '#9b59b6' }}>
            <ArrowUpwardIcon sx={{ fontSize: 13 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onMove('down')} disabled={isLast}
            sx={{ p: '2px', color: isLast ? '#e0e0e0' : '#9b59b6' }}>
            <ArrowDownwardIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </div>

        {/* Name + location info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }} noWrap>{name}</Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 3 }}>
            {(attr.city || attr.country) && (
              <Typography sx={{ fontSize: 11, color: '#555' }}>
                📍 {[attr.city, attr.country].filter(Boolean).join(' · ')}
              </Typography>
            )}
            <Typography sx={{ fontSize: 10, color: '#aaa' }}>
              {attr.lat.toFixed(4)}°, {attr.lng.toFixed(4)}°
            </Typography>
            {transport && (
              <Typography sx={{ fontSize: 11, color: '#9e9e9e' }}>
                {transport.emoji} {isZh ? transport.zh : transport.en}
              </Typography>
            )}
          </div>
        </div>

        {/* Thumbnail */}
        {attr.imageUrl && (
          <img src={attr.imageUrl} alt={name}
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, alignItems: 'center' }}>
          {attr.wikidataUrl && (
            <Tooltip title={isZh ? '了解更多' : 'More info'}>
              <a href={attr.wikidataUrl} target="_blank" rel="noreferrer"
                style={{ color: '#9b59b6', display: 'flex', lineHeight: 1 }}
                onClick={e => e.stopPropagation()}>
                <OpenInNewIcon sx={{ fontSize: 15 }} />
              </a>
            </Tooltip>
          )}
          <Tooltip title={isZh ? '移除' : 'Remove'}>
            <IconButton size="small" onClick={onRemove}
              sx={{ p: '2px', color: '#bdbdbd', '&:hover': { color: '#e53935' } }}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Editable fields ── */}
      <div style={{ padding: '4px 12px 10px', paddingLeft: 46 }}>
        <TextField
          size="small"
          placeholder={isZh ? '旅程說明（選填）' : 'Notes (optional)'}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={() => onUpdateField('notes', notes.trim() || undefined)}
          fullWidth multiline maxRows={3}
          sx={{
            mb: 0.8,
            '& .MuiInputBase-input': { fontSize: 12 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f0e6ff' },
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography sx={{ fontSize: 11, color: '#777', whiteSpace: 'nowrap' }}>
            👥 {isZh ? '參與人數' : 'Participants'}
          </Typography>
          <TextField
            size="small" type="number"
            value={parts}
            onChange={e => setParts(e.target.value)}
            onBlur={() => {
              const n = parts === '' ? undefined : Number(parts);
              onUpdateField('participants', n);
            }}
            inputProps={{ min: 1, max: 999 }}
            sx={{
              width: 72,
              '& .MuiInputBase-input': { fontSize: 12, textAlign: 'center', py: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f0e6ff' },
            }}
          />
        </div>

        {/* ── Photo strip ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, alignItems: 'center' }}>
          {(attr.photos ?? []).map(p => {
            const { authSrc, plainSrc } = resolveTripPhoto(p);
            const imgStyle = { width: 72, height: 72, objectFit: 'cover' as const, borderRadius: 8, display: 'block' };
            return (
            <div key={tripPhotoKey(p)} style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
              {authSrc
                ? <AuthImage src={authSrc} alt="" style={imgStyle} />
                : <img src={plainSrc} alt="" style={imgStyle} />}
              <button
                onClick={() => handleRemovePhoto(p)}
                style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 18, height: 18, borderRadius: '50%', padding: 0, lineHeight: 1,
                  background: 'rgba(0,0,0,0.55)', border: 'none', cursor: 'pointer',
                  color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            );
          })}
          <Tooltip title={isZh ? '新增照片' : 'Add photo'}>
            <IconButton size="small"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploading}
              sx={{
                width: 72, height: 72, borderRadius: 2,
                border: '1.5px dashed #d0b8f0',
                color: '#9b59b6',
                '&:hover': { background: '#f9f0ff' },
              }}
            >
              {uploading ? <CircularProgress size={18} sx={{ color: '#9b59b6' }} /> : <CameraAltIcon sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
          <input ref={photoInputRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handlePhotoUpload} />
        </div>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

const TripPlannerPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = !i18n.language.startsWith('en');
  const { showApiError, showSuccess } = useMessage();

  const [plans, setPlans]                   = useState<TripPlan[]>([]);
  const [loading, setLoading]               = useState(true);
  const [selectedId, setSelectedId]         = useState<string | null>(null);
  const [newPlanDlg, setNewPlanDlg]         = useState(false);
  const [newName, setNewName]               = useState('');
  const [renamingId, setRenamingId]         = useState<string | null>(null);
  const [renameVal, setRenameVal]           = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [dayWeather, setDayWeather]         = useState<Map<string, DayForecast>>(new Map());
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef                       = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadLifeData()
      .then(d => {
        const loaded = normalizeTrips(d.trip_plans ?? []);
        setPlans(loaded);
        setSelectedId(loaded[0]?.id ?? null);
      })
      .catch(e => { console.warn('[TripPlanner] load failed:', e); showApiError(e); })
      .finally(() => setLoading(false));
  }, []);

  const persist = async (updated: TripPlan[]) => {
    setPlans(updated);
    await saveTripPlans(updated).catch(e => { console.warn('[TripPlanner] save failed:', e); showApiError(e); });
  };

  const handleCreatePlan = async () => {
    const name = newName.trim();
    if (!name) return;
    const plan: TripPlan = { id: `plan_${Date.now()}`, name, attractions: [], createdAt: Date.now() };
    await persist([...plans, plan]);
    showSuccess('SYS310003');
    setSelectedId(plan.id);
    setNewPlanDlg(false);
    setNewName('');
  };

  const handleDeletePlan = async (id: string) => {
    const updated = plans.filter(p => p.id !== id);
    await persist(updated);
    showSuccess('SYS310002');
    if (selectedId === id) setSelectedId(updated[0]?.id ?? null);
    setDeleteConfirmId(null);
  };

  const handleRenameConfirm = async () => {
    if (!renamingId || !renameVal.trim()) return;
    await persist(plans.map(p => p.id === renamingId ? { ...p, name: renameVal.trim() } : p));
    setRenamingId(null);
  };

  const handleRemoveAttraction = async (planId: string, attrId: string, date: string) => {
    await persist(plans.map(p =>
      p.id !== planId ? p : {
        ...p,
        attractions: p.attractions.filter(a => !(a.id === attrId && a.date === date)),
      }
    ));
  };

  const handleMoveAttraction = async (planId: string, date: string, attrId: string, dir: 'up' | 'down') => {
    await persist(plans.map(p => {
      if (p.id !== planId) return p;
      const dayAttrs = p.attractions
        .filter(a => a.date === date)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = dayAttrs.findIndex(a => a.id === attrId);
      if (idx === -1) return p;
      const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= dayAttrs.length) return p;
      const newDay = [...dayAttrs];
      [newDay[idx], newDay[swapIdx]] = [newDay[swapIdx], newDay[idx]];
      const orderMap = new Map(newDay.map((a, i) => [`${a.id}:${date}`, i + 1]));
      return {
        ...p,
        attractions: p.attractions.map(a =>
          a.date === date ? { ...a, order: orderMap.get(`${a.id}:${date}`) ?? a.order } : a
        ),
      };
    }));
  };

  const handleUpdateField = async (planId: string, date: string, attrId: string, field: string, value: any) => {
    await persist(plans.map(p =>
      p.id !== planId ? p : {
        ...p,
        attractions: p.attractions.map(a =>
          a.id === attrId && a.date === date ? { ...a, [field]: value } as TripAttraction : a
        ),
      }
    ));
  };

  const handleUpdatePlanCover = async (planId: string, cover: TripPhoto | undefined) => {
    await persist(plans.map(p => p.id === planId ? { ...p, coverPhotoUrl: cover } : p));
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedId) return;
    setCoverUploading(true);
    try {
      const url = await uploadTripPhoto(file);
      await handleUpdatePlanCover(selectedId, url);
      showSuccess('SYS310004');
    } catch (e) { showApiError(e); }
    finally { setCoverUploading(false); e.target.value = ''; }
  };

  const selectedPlan = plans.find(p => p.id === selectedId) ?? null;

  const dateGroups = useMemo(() => {
    if (!selectedPlan) return [];
    const map = new Map<string, TripAttraction[]>();
    for (const a of selectedPlan.attractions) {
      const list = map.get(a.date) ?? [];
      list.push(a);
      map.set(a.date, list);
    }
    return Array.from(map.keys()).sort().map(date => ({
      date,
      attractions: (map.get(date)!).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }));
  }, [selectedPlan]);

  const uniqueDates = useMemo(
    () => dateGroups.map(g => g.date).join(','),
    [dateGroups],
  );

  // ── Background geocoding backfill ────────────────────────────────────────────
  // Fills in country/city for stops that were added before Nominatim was wired up.
  // Runs once per plan selection; Nominatim rate-limit: 1 req/s.
  useEffect(() => {
    const plan = plans.find(p => p.id === selectedId);
    if (!plan) return;

    // Deduplicate by OSM id — same place may appear on multiple days
    const seen = new Set<string>();
    const queue: Array<{ id: string; lat: number; lng: number }> = [];
    for (const a of plan.attractions) {
      if (!a.country && !a.city && !seen.has(a.id)) {
        seen.add(a.id);
        queue.push({ id: a.id, lat: a.lat, lng: a.lng });
      }
    }
    if (queue.length === 0) return;

    let cancelled = false;
    const results = new Map<string, { country?: string; city?: string }>();

    (async () => {
      for (let i = 0; i < queue.length; i++) {
        if (cancelled) break;
        const { id, lat, lng } = queue[i];
        try {
          const ctrl = new AbortController();
          const tid  = setTimeout(() => ctrl.abort(), 3000);
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
            { headers: { 'Accept-Language': 'zh-TW,zh;q=0.9,en;q=0.8' }, signal: ctrl.signal },
          );
          clearTimeout(tid);
          if (res.ok) {
            const geo     = await res.json();
            const country = geo.address?.country as string | undefined;
            const city    = (geo.address?.city ?? geo.address?.town ?? geo.address?.village
              ?? geo.address?.municipality ?? geo.address?.county) as string | undefined;
            results.set(id, { country, city });
          }
        } catch { /* ignore */ } // silent ok: geocode failure is non-blocking, background enrichment

        if (!cancelled && i < queue.length - 1) {
          await new Promise(r => setTimeout(r, 1200)); // Nominatim 1 req/s
        }
      }

      if (!cancelled && results.size > 0) {
        setPlans(prev => {
          const next = prev.map(p => {
            if (p.id !== selectedId) return p;
            return {
              ...p,
              attractions: p.attractions.map(a => {
                const upd = results.get(a.id);
                return upd && !a.country && !a.city ? { ...a, ...upd } : a;
              }),
            };
          });
          // silent ok: geocode backfill save failure is non-blocking
          saveTripPlans(next).catch(e => console.warn('[TripPlanner] geocode backfill save failed:', e));
          return next;
        });
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Reset cached weather when switching plans
  useEffect(() => { setDayWeather(new Map()); }, [selectedId]);

  // Fetch weather for each day (uses attractionService cache; cheap on repeated calls)
  useEffect(() => {
    if (!uniqueDates) return;
    dateGroups.forEach(({ date, attractions: dayAttrs }) => {
      if (dayAttrs.length === 0) return;
      // Use centroid of the day's attractions as the weather query point
      const lat = dayAttrs.reduce((s, a) => s + a.lat, 0) / dayAttrs.length;
      const lng = dayAttrs.reduce((s, a) => s + a.lng, 0) / dayAttrs.length;
      fetchWeatherForDate(lat, lng, date, 1)
        .then(forecast => {
          const f = forecast[0];
          if (f) setDayWeather(prev => new Map(prev).set(date, f));
        })
        .catch(() => { /* weather is decorative — ignore errors */ });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueDates]);

  const dateRange = dateGroups.length >= 2
    ? `${dateGroups[0].date} – ${dateGroups[dateGroups.length - 1].date}`
    : dateGroups.length === 1 ? dateGroups[0].date : '';

  // Region groups: (country, city) → days → stops (with global day number / isFirst / isLast)
  const regionGroups = useMemo(() => {
    if (!selectedPlan) return [];

    // Build global sorted attractions per date
    const globalByDate = new Map<string, TripAttraction[]>();
    for (const a of selectedPlan.attractions) {
      const list = globalByDate.get(a.date) ?? [];
      list.push(a);
      globalByDate.set(a.date, list);
    }
    Array.from(globalByDate.entries()).forEach(([d, attrs]) => {
      globalByDate.set(d, [...attrs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    });
    const dayNum = new Map(
      Array.from(globalByDate.keys()).sort().map((d, i) => [d, i + 1]),
    );

    // Group by (country, city) — NUL separator avoids collisions
    type RegionData = { country?: string; city?: string; byDate: Map<string, TripAttraction[]> };
    const rMap = new Map<string, RegionData>();
    for (const a of selectedPlan.attractions) {
      const key = `${a.country ?? ''}\x00${a.city ?? ''}`;
      if (!rMap.has(key)) rMap.set(key, { country: a.country, city: a.city, byDate: new Map() });
      const rd = rMap.get(key)!;
      const list = rd.byDate.get(a.date) ?? [];
      list.push(a);
      rd.byDate.set(a.date, list);
    }

    return Array.from(rMap.values())
      .map(({ country, city, byDate }) => {
        const days = Array.from(byDate.keys())
          .sort()
          .map(date => {
            const globalDay = globalByDate.get(date) ?? [];
            const regionDay = [...(byDate.get(date) ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            return {
              date,
              dayNumber: dayNum.get(date) ?? 0,
              attractions: regionDay.map(a => ({
                attr: a,
                globalIdx: globalDay.findIndex(g => g.id === a.id),
                globalTotal: globalDay.length,
              })),
            };
          });
        return { country, city, days };
      })
      .sort((a, b) => (a.days[0]?.date ?? '').localeCompare(b.days[0]?.date ?? ''));
  }, [selectedPlan]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <CircularProgress sx={{ color: '#9b59b6' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f5f7fa' }}>

      {/* ── Left: plan list ─────────────────────────────────────────── */}
      <div style={{
        width: 260, flexShrink: 0,
        background: '#fff',
        borderRight: '1px solid #e0e0e0',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '20px 16px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <RouteIcon sx={{ color: '#9b59b6' }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>
            {isZh ? '旅程計畫' : 'Trip Planner'}
          </Typography>
          <Tooltip title={isZh ? '新旅程' : 'New Trip'}>
            <IconButton size="small" onClick={() => setNewPlanDlg(true)}
              sx={{ background: '#f3e5f5', color: '#9b59b6', '&:hover': { background: '#e1bee7' } }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <Divider />

        {plans.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9e9e9e' }}>
            <RouteIcon sx={{ fontSize: 40, color: '#e0e0e0', marginBottom: 8 }} />
            <Typography sx={{ fontSize: 13 }}>
              {isZh
                ? '尚無旅程。在地球視圖右鍵景點加入，或按 + 建立空計畫。'
                : 'No trips. Right-click attractions on the Globe, or click + to create.'}
            </Typography>
          </div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
            {plans.map(plan => {
              const total = plan.attractions.length;
              const dates = Array.from(new Set(plan.attractions.map(a => a.date))).sort();
              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedId(plan.id)}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    borderLeft: selectedId === plan.id ? '3px solid #9b59b6' : '3px solid transparent',
                    background: selectedId === plan.id ? '#f9f0ff' : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {renamingId === plan.id ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <TextField
                        size="small"
                        value={renameVal}
                        onChange={e => setRenameVal(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleRenameConfirm()}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 13, py: 0.5 } }}
                      />
                      <IconButton size="small" onClick={e => { e.stopPropagation(); handleRenameConfirm(); }}>
                        <CheckIcon fontSize="small" sx={{ color: '#9b59b6' }} />
                      </IconButton>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }} noWrap>{plan.name}</Typography>
                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                          <IconButton size="small"
                            onClick={() => { setRenamingId(plan.id); setRenameVal(plan.name); }}>
                            <EditIcon sx={{ fontSize: 14, color: '#bdbdbd' }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteConfirmId(plan.id)}>
                            <DeleteIcon sx={{ fontSize: 14, color: '#bdbdbd' }} />
                          </IconButton>
                        </div>
                      </div>
                      {dates.length > 0 && (
                        <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.3 }}>
                          {dates[0]}{dates.length > 1 ? ` – ${dates[dates.length - 1]}` : ''}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 11, color: '#ce93d8', mt: 0.2 }}>
                        {dates.length} {isZh ? '天' : 'days'} · {total} {isZh ? '個景點' : 'stops'}
                      </Typography>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Right: plan detail ──────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selectedPlan ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9e9e9e' }}>
            <div style={{ textAlign: 'center' }}>
              <RouteIcon sx={{ fontSize: 64, color: '#e0e0e0', marginBottom: 16 }} />
              <Typography>{isZh ? '選擇左側旅程或建立新旅程' : 'Select a trip or create a new one'}</Typography>
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
              {/* Cover photo banner */}
              {selectedPlan.coverPhotoUrl ? (
                <div style={{ position: 'relative', width: '100%', height: 140 }}>
                  {(() => {
                    const { authSrc, plainSrc } = resolveTripPhoto(selectedPlan.coverPhotoUrl);
                    const cs = { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' };
                    return authSrc
                      ? <AuthImage src={authSrc} alt="cover" style={cs} />
                      : <img src={plainSrc} alt="cover" style={cs} />;
                  })()}
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    <button onClick={() => coverInputRef.current?.click()} style={{
                      background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 6,
                      color: '#fff', fontSize: 11, padding: '4px 8px', cursor: 'pointer',
                    }}>
                      {isZh ? '更換' : 'Change'}
                    </button>
                    <button onClick={() => handleUpdatePlanCover(selectedPlan.id, undefined)} style={{
                      background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 6,
                      color: '#fff', fontSize: 11, padding: '4px 8px', cursor: 'pointer',
                    }}>✕</button>
                  </div>
                </div>
              ) : null}

              <div style={{ padding: '16px 28px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', flex: 1 }}>
                    {selectedPlan.name}
                  </Typography>
                  <Tooltip title={isZh ? '設定封面照片' : 'Set cover photo'}>
                    <IconButton size="small" onClick={() => coverInputRef.current?.click()}
                      disabled={coverUploading}
                      sx={{ color: '#bdbdbd', '&:hover': { color: '#9b59b6' } }}>
                      {coverUploading
                        ? <CircularProgress size={16} sx={{ color: '#9b59b6' }} />
                        : <CameraAltIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {dateRange && (
                    <Chip
                      icon={<CalendarMonthIcon />}
                      label={dateRange}
                      size="small"
                      sx={{ background: '#f3e5f5', color: '#7b1fa2', fontSize: 12 }}
                    />
                  )}
                  <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>
                    {dateGroups.length} {isZh ? '天' : 'days'} · {selectedPlan.attractions.length} {isZh ? '個景點' : 'stops'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#bdbdbd' }}>
                    {isZh ? '在地球視圖右鍵景點可加入計畫' : 'Right-click spots on Globe to add to plan'}
                  </Typography>
                </div>
              </div>
              <input ref={coverInputRef} type="file" accept="image/*"
                style={{ display: 'none' }} onChange={handleCoverUpload} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              {selectedPlan.attractions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9e9e9e' }}>
                  <PlaceIcon sx={{ fontSize: 48, color: '#e0e0e0', marginBottom: 16 }} />
                  <Typography>
                    {isZh ? '在地球視圖右鍵景點加入此旅程' : 'Right-click attractions on the Globe to add stops'}
                  </Typography>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                  {regionGroups.map(({ country, city, days }) => {
                    const regionLabel = [city, country].filter(Boolean).join(' · ');
                    const regionKey = `${country ?? ''}-${city ?? ''}`;
                    const regionTotal = days.reduce((s, d) => s + d.attractions.length, 0);
                    return (
                      <div key={regionKey}>
                        {/* ── Region header ── */}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          marginBottom: 14,
                          background: 'linear-gradient(135deg, #e8f5e9 0%, #f3e5f5 100%)',
                          borderRadius: 10, padding: '8px 14px',
                          border: '1px solid #d0e8d0',
                        }}>
                          <span style={{ fontSize: 18 }}>🌍</span>
                          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#2e7d32', flex: 1 }}>
                            {regionLabel || (isZh ? '未分區地點' : 'Unlocated')}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#9e9e9e' }}>
                            {days.length} {isZh ? '天' : 'd'} · {regionTotal} {isZh ? '站' : 'stops'}
                          </Typography>
                        </div>

                        {/* ── Days within region ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingLeft: 14 }}>
                          {days.map(({ date, dayNumber, attractions: dayItems }) => (
                            <div key={date}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                marginBottom: 10,
                                borderBottom: '2px solid #f3e5f5', paddingBottom: 6,
                              }}>
                                <div style={{
                                  background: '#9b59b6', color: '#fff',
                                  borderRadius: 6, padding: '2px 10px',
                                  fontSize: 12, fontWeight: 700,
                                }}>
                                  Day {dayNumber}
                                </div>
                                <Typography sx={{ fontSize: 13, color: '#555', fontWeight: 600 }}>
                                  📅 {date}
                                </Typography>
                                {(() => {
                                  const wx = dayWeather.get(date);
                                  if (!wx) return null;
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ fontSize: 16 }}>{wx.emoji}</span>
                                      <Typography sx={{ fontSize: 12, color: '#e57373', fontWeight: 600 }}>
                                        {wx.tempMax}°
                                      </Typography>
                                      <Typography sx={{ fontSize: 12, color: '#90a4ae' }}>
                                        / {wx.tempMin}°
                                      </Typography>
                                      {wx.precipitation > 0 && (
                                        <Typography sx={{ fontSize: 11, color: '#64b5f6' }}>
                                          💧{wx.precipitation}mm
                                        </Typography>
                                      )}
                                    </div>
                                  );
                                })()}
                                <Typography sx={{ fontSize: 11, color: '#9e9e9e', marginLeft: 'auto' }}>
                                  {dayItems.length} {isZh ? '個景點' : 'stops'}
                                </Typography>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 12 }}>
                                {dayItems.map(({ attr, globalIdx, globalTotal }) => (
                                  <StopCard
                                    key={`${attr.id}-${date}-${globalIdx}`}
                                    attr={attr}
                                    index={globalIdx}
                                    isFirst={globalIdx === 0}
                                    isLast={globalIdx === globalTotal - 1}
                                    isZh={isZh}
                                    onMove={dir => handleMoveAttraction(selectedPlan.id, date, attr.id, dir)}
                                    onUpdateField={(f, v) => handleUpdateField(selectedPlan.id, date, attr.id, f, v)}
                                    onRemove={() => handleRemoveAttraction(selectedPlan.id, attr.id, date)}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── New Plan Dialog ─────────────────────────────────────────── */}
      <Dialog open={newPlanDlg} onClose={() => setNewPlanDlg(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {isZh ? '建立新旅程' : 'Create New Trip'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={isZh ? '旅程名稱' : 'Trip Name'}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreatePlan()}
            size="small" fullWidth autoFocus
            placeholder={isZh ? '例如：日本春遊 2026' : 'e.g. Japan Spring 2026'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNewPlanDlg(false)} sx={{ color: '#757575' }}>
            {isZh ? '取消' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePlan}
            disabled={!newName.trim()}
            sx={{ background: '#9b59b6', '&:hover': { background: '#8e44ad' } }}
          >
            {isZh ? '建立' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ───────────────────────────────────── */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isZh ? '確認刪除' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {isZh
              ? `確定要刪除「${plans.find(p => p.id === deleteConfirmId)?.name}」嗎？此操作無法復原。`
              : `Delete "${plans.find(p => p.id === deleteConfirmId)?.name}"? This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ color: '#757575' }}>
            {isZh ? '取消' : 'Cancel'}
          </Button>
          <Button variant="contained" color="error"
            onClick={() => deleteConfirmId && handleDeletePlan(deleteConfirmId)}>
            {isZh ? '刪除' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TripPlannerPage;
